import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { LightThemeTable } from "components/LightThemeTable";
import { State } from "state";
import { getRoles } from "state/chain_user_role";
import Constants from "helpers/constants";
import { createRequest } from "state/request";
import Loader from "components/Loader";
import CommonModal from "components/CommonModal";

interface UserData {
    email: string;
    stores?: any[];
    role?: string;
    selected: boolean;
}

interface ExternalAdminChainRequestModalProps {
    isOpen: boolean;
    onClose: () => void;
    users?: any[];
    chainName?: string;
    chainId?: string | number;
    projectName?: string;
    projectId?: string | number;
    stores?: string[];
    uploadedFile?: File | null;
    allUserEmails?: string[];
}

interface ComponentState {
    showTargetUsers: boolean;
    tableData: UserData[];
    selectAll: boolean;
    validationErrors: {
        [key: number]: {
            role?: boolean;
        }
    };
    isSubmitting: boolean;
    isLoading: boolean;
}

const styles = {
    header: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "10px",
    },
    icon: {
        width: "40px",
        height: "40px",
        backgroundColor: "#F5A623",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    message: {
        fontSize: "12px",
        color: "#666",
        marginBottom: "10px",
        lineHeight: 1.5,
    },
    infoPill: {
        backgroundColor: "#E8F0FB",
        border: "1px solid #0755AA",
        borderRadius: "10px",
        padding: "10px 16px",
        fontSize: "12px",
        color: "#0755AA",
        fontWeight: 500,
        marginBottom: "10px",
        textAlign: "left" as const,
    },
    buttonsContainer: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "12px",
        marginTop: "24px",
    },
    tableContainer: {
        marginBottom: "10px",
    },
    cellStyle: {
        fontSize: "12px",
        color: "#374151",
    } as React.CSSProperties,
    checkbox: {
        width: "20px",
        height: "20px",
        cursor: "pointer",
        accentColor: "#F5A623",
    },
    errorMessage: {
        fontSize: "12px",
        color: "#EF4444",
        margin: "4px 0 0 0",
    },
} as const;

// Warning Icon component
const WarningIcon: React.FC = () => (
    <div style={styles.icon}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
                d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0378 2.66667 10.268 4L3.33978 16C2.56998 17.3333 3.53223 19 5.07183 19Z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </div>
);

type CombinedProps = ExternalAdminChainRequestModalProps & PropsFromRedux;

class ExternalAdminChainRequestModal extends React.Component<CombinedProps, ComponentState> {
    constructor(props: CombinedProps) {
        super(props);
        this.state = {
            showTargetUsers: false,
            tableData: [],
            selectAll: false,
            validationErrors: {},
            isSubmitting: false,
            isLoading: false,
        };
    }

    componentDidMount() {
        // Add custom checkbox style
        const styleId = 'custom-checkbox-style-external';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
        input[type="checkbox"] {
          accent-color: #F5A623;
        }
      `;
            document.head.appendChild(style);
        }

        // Fetch roles
        this.props.getRoles();

        // Parse data on mount so stores are ready for confirmation table
        this.parseAndSetData();
    }

    componentDidUpdate(prevProps: CombinedProps) {
        // Reset showTargetUsers when modal closes
        if (prevProps.isOpen && !this.props.isOpen) {
            this.setState({ showTargetUsers: false });
        }

        // Re-parse data if users or uploadedFile changes
        if (prevProps.users !== this.props.users || prevProps.uploadedFile !== this.props.uploadedFile) {
            this.parseAndSetData();
        }
    }

    normalizeEmail = (email: string | undefined) => String(email || "").trim().toLowerCase();

    dedupeUsersByEmail = (users: any[]) => {
        const seen = new Set<string>();
        return users.filter((user) => {
            const normalized = this.normalizeEmail(user.email || user.id);
            if (!normalized || seen.has(normalized)) {
                return false;
            }
            seen.add(normalized);
            return true;
        });
    };

    parseAndSetData = async () => {
        const { uploadedFile, users } = this.props;
        const uniqueUsers = users ? this.dedupeUsersByEmail(users) : [];

        if (!uploadedFile || !uniqueUsers || uniqueUsers.length === 0) {
            // Initialize without CSV parsing
            const shouldAutoSelect = uniqueUsers && uniqueUsers.length === 1;
            const data: UserData[] = uniqueUsers?.map((user) => ({
                email: user.email || user.id,
                stores: user.stores || [],
                role: "",
                selected: shouldAutoSelect || false,
            })) || [];
            this.setState({
                tableData: data,
                selectAll: shouldAutoSelect || false,
            });
            return;
        }

        this.setState({ isLoading: true });

        try {
            const reader = new FileReader();

            reader.onload = (e: any) => {
                try {
                    const raw = e.target.result as ArrayBuffer;
                    const bytes = new Uint8Array(raw);
                    let enc = "utf-8";
                    if (bytes[0] === 0xff && bytes[1] === 0xfe) enc = "utf-16le";
                    else if (bytes[0] === 0xfe && bytes[1] === 0xff) enc = "utf-16be";
                    else if (!(bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)) {
                      let valid = true;
                      for (let i = 0; i < Math.min(bytes.length, 4096); i++) {
                        if (bytes[i] > 0x7f && bytes[i] < 0xa0) { valid = false; break; }
                      }
                      if (!valid) enc = "windows-1252";
                    }
                    const text = new TextDecoder(enc).decode(raw);
                    const lines = text.split("\n").filter((line) => line.trim() !== "");
                    const emailToStoresMap = new Map<string, string[]>();

                    if (lines.length > 0) {
                        const headers = lines[0].split(",").map((col: string) => col.trim());
                        const emailIndex = headers.findIndex((header) => header === "Email Address");
                        const storeIndex = headers.findIndex((header) => header === "Retail Store Number");

                        if (emailIndex !== -1 && storeIndex !== -1) {
                            for (let i = 1; i < lines.length; i++) {
                                const row = lines[i].split(",").map((cell) => cell.trim());
                                const email = this.normalizeEmail(row[emailIndex]);
                                const store = row[storeIndex];

                                if (email && store) {
                                    if (!emailToStoresMap.has(email)) {
                                        emailToStoresMap.set(email, []);
                                    }
                                    if (!emailToStoresMap.get(email)!.includes(store)) {
                                        emailToStoresMap.get(email)!.push(store);
                                    }
                                }
                            }
                        }
                    }

                    // Map users to their stores
                    const shouldAutoSelect = uniqueUsers.length === 1;
                    const data: UserData[] = uniqueUsers.map((user) => {
                        const userEmailVal = user.email || user.id;
                        const storesFromCSV = emailToStoresMap.get(this.normalizeEmail(userEmailVal)) || [];

                        // Use stores from CSV if available
                        const finalStores = storesFromCSV.length > 0
                            ? storesFromCSV.map((storeNum) => ({
                                retail_store_num: storeNum,
                            }))
                            : (user.stores || []);

                        return {
                            email: userEmailVal,
                            stores: finalStores,
                            role: "",
                            selected: shouldAutoSelect,
                        };
                    });

                    this.setState({
                        tableData: data,
                        selectAll: shouldAutoSelect,
                        isLoading: false,
                    });
                } catch (error) {
                    console.error("Error parsing CSV:", error);
                    this.setState({ isLoading: false });
                }
            };

            reader.onerror = () => {
                console.error("Error reading file");
                this.setState({ isLoading: false });
            };

            reader.readAsArrayBuffer(uploadedFile);
        } catch (error) {
            console.error("Error in parseAndSetData:", error);
            this.setState({ isLoading: false });
        }
    };

    getUserRoleId = (roleName: string) => {
        const { roles } = this.props;
        const roleObj = roles?.find((role: any) => role.role === roleName);
        return roleObj?.id || null;
    };

    handleSubmit = async () => {
        const { tableData } = this.state;
        const { onClose, createRequest, chainId } = this.props;

        // Get auth user from Redux state
        const authUser = (this.props as any).authUser;
        const userId = authUser?.id;
        const userEmail = authUser?.email;

        // Check if any selected user is missing required fields
        const errors: {
            [key: number]: {
                role?: boolean;
            }
        } = {};
        let hasErrors = false;

        tableData.forEach((user, index) => {
            if (user.selected) {
                const userErrors: { role?: boolean } = {};
                if (!user.role || user.role.trim() === "") {
                    userErrors.role = true;
                    hasErrors = true;
                }
                if (Object.keys(userErrors).length > 0) {
                    errors[index] = userErrors;
                }
            }
        });

        if (hasErrors) {
            this.setState({ validationErrors: errors });
            return;
        }

        // Get only selected users with valid data and dedupe by email
        const selectedUsers: UserData[] = [];
        const seenEmails = new Set<string>();
        tableData.forEach((user) => {
            if (user.selected && user.role && user.role.trim() !== "") {
                const normalized = this.normalizeEmail(user.email);
                if (!normalized || seenEmails.has(normalized)) {
                    return;
                }
                seenEmails.add(normalized);
                selectedUsers.push(user);
            }
        });

        // Prepare API payload
        const apiPayload = {
            requestType: Constants.CHAIN_ACCESS_BY_EXTERNAL_PROJECT_ADMIN,
            context: {
                initiator_user_id: userId,
                initiator_email: userEmail,
                request_intiated_from: "CREATE PROJECT USER CHAIN MAPPING",
                payload_from: "REQUESTOR"
            },
            data: {
                CHAIN_ACCESS_BY_EXTERNAL_PROJECT_ADMIN: selectedUsers.map(user => ({
                    chain_id: Number(chainId),
                    user_email: user.email,
                    user_role_id: this.getUserRoleId(user.role!),
                    user_role: user.role!,
                    stores: user.stores?.map((store: any) =>
                        store.retail_store_num || store
                    ) || []
                }))
            }
        };

        this.setState({ isSubmitting: true });
        try {
            await createRequest(apiPayload);
            onClose();
        } catch (error) {
            console.error("Error creating request approval:", error);
        } finally {
            this.setState({ isSubmitting: false });
        }
    };

    handleSelectAll = () => {
        const { selectAll, tableData } = this.state;
        const newSelectAll = !selectAll;
        this.setState({
            selectAll: newSelectAll,
            tableData: tableData.map((user) => ({ ...user, selected: newSelectAll })),
        });
    };

    handleUserSelect = (index: number) => {
        const { tableData, validationErrors } = this.state;
        const newTableData = [...tableData];
        newTableData[index].selected = !newTableData[index].selected;

        const newSelectAll = newTableData.every((user) => user.selected);

        // Clear validation errors if unchecking
        let newErrors = { ...validationErrors };
        if (!newTableData[index].selected) {
            delete newErrors[index];
        }

        this.setState({
            tableData: newTableData,
            selectAll: newSelectAll,
            validationErrors: newErrors,
        });
    };

    handleRoleChange = (index: number, value: string) => {
        const { tableData, validationErrors } = this.state;
        const updatedData = [...tableData];
        updatedData[index].role = value;

        // Clear validation error if role is selected
        let newErrors = { ...validationErrors };
        if (value && value.trim() !== "") {
            if (newErrors[index]) {
                delete newErrors[index].role;
                if (Object.keys(newErrors[index]).length === 0) {
                    delete newErrors[index];
                }
            }
        }

        this.setState({ tableData: updatedData, validationErrors: newErrors });
    };

    handleYesClick = () => {
        this.setState({ showTargetUsers: true });
    };

    getColumns = () => {
        const { selectAll, validationErrors } = this.state;
        const { roles } = this.props;

        return [
            {
                field: "checkbox",
                header: (
                    <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={this.handleSelectAll}
                        style={styles.checkbox}
                    />
                ),
                formatter: (_: any, row: UserData, index: number) => (
                    <input
                        type="checkbox"
                        checked={row.selected}
                        onChange={() => this.handleUserSelect(index)}
                        style={styles.checkbox}
                    />
                ),
            },
            {
                field: "email",
                header: "Email",
                formatter: (_: any, row: any) => (
                    <div style={{ ...styles.cellStyle, fontWeight: 500 }}>
                        {row.email || row.id || "N/A"}
                    </div>
                ),
            },
            {
                field: "stores",
                header: "Associated Stores",
                formatter: (_: any, row: UserData) => {
                    if (!row.stores || row.stores.length === 0) {
                        return <div style={{ ...styles.cellStyle, color: "#9CA3AF" }}>No stores found</div>;
                    }

                    // Show stores as comma-separated text
                    const storesArr = Array.isArray(row.stores) ? row.stores : [];
                    const storeNumbers = storesArr.map(store => {
                        if (typeof store === 'string' || typeof store === 'number') {
                            return String(store);
                        } else if (typeof store === 'object' && store !== null) {
                            return store.retail_store_num || store.store_num || store.storeNum || store.store || store.id || '';
                        }
                        return '';
                    }).filter(s => s !== '').join(", ");

                    return <div style={styles.cellStyle}>{storeNumbers || "No stores"}</div>;
                },
            },
            {
                field: "role",
                header: "Role",
                formatter: (_: any, row: UserData, index: number) => (
                    <div style={{ width: '100%' }}>
                        <select
                            style={{
                                padding: "6px 10px",
                                border: validationErrors[index]?.role ? "2px solid #EF4444" : "1px solid #D1D5DB",
                                borderRadius: "6px",
                                fontSize: "12px",
                                color: row.role ? "#374151" : "#9CA3AF",
                                cursor: "pointer",
                                backgroundColor: "white",
                                minWidth: "150px",
                                width: "100%",
                            }}
                            value={row.role || ""}
                            onChange={(e) => this.handleRoleChange(index, e.target.value)}
                        >
                            <option value="" disabled>Select Role</option>
                            {roles?.filter((role: any) => role.role !== Constants.AB_SYSTEM_ADMIN)?.map((role: any) => (
                                <option key={role.id} value={role.role}>
                                    {role.role}
                                </option>
                            ))}
                        </select>
                        {validationErrors[index]?.role && row.selected && (
                            <p style={styles.errorMessage}>Please assign a role to this user</p>
                        )}
                    </div>
                ),
            },
        ];
    };

    // Columns for confirmation table (email + stores, read-only)
    getConfirmationColumns = () => [
        {
            field: "email",
            header: "Email",
            formatter: (_: any, row: any) => (
                <div style={{ ...styles.cellStyle, fontWeight: 500 }}>
                    {row.email || row.id || "N/A"}
                </div>
            ),
        },
        {
            field: "stores",
            header: "Associated Stores",
            formatter: (_: any, row: any) => {
                if (!row.stores || row.stores.length === 0) {
                    return <div style={{ ...styles.cellStyle, color: "#9CA3AF" }}>No stores found</div>;
                }
                const storesArr2 = Array.isArray(row.stores) ? row.stores : [];
                const storeNumbers = storesArr2.map((store: any) => {
                    if (typeof store === 'string' || typeof store === 'number') return String(store);
                    if (typeof store === 'object' && store !== null) {
                        return store.retail_store_num || store.store_num || store.storeNum || store.store || store.id || '';
                    }
                    return '';
                }).filter((s: string) => s !== '').join(", ");
                return <div style={styles.cellStyle}>{storeNumbers || "No stores"}</div>;
            },
        },
    ];

    render() {
        const { isOpen, onClose, users, chainName } = this.props;
        const { tableData, isSubmitting, isLoading, showTargetUsers } = this.state;

        const hasUnmappedUsers = users && users.length > 0;
        const hasSelectedUsers = tableData.some(user => user.selected);
        const columns = this.getColumns();
        const confirmationColumns = this.getConfirmationColumns();

        // Confirmation users list (just emails for display)
        // Use tableData so CSV-parsed stores are reflected in the confirmation table
        const confirmationData: UserData[] = tableData.length > 0 ? tableData : (users?.map(u => ({ email: u.email || u.id, stores: u.stores || [], selected: false })) || []);

        // Modal header content
        const headerContent = (
            <div style={styles.header}>
                <WarningIcon />
                <h2 style={{ fontWeight: 500, fontSize: "16px" }}>User(s) Mapping Request</h2>
            </div>
        );

        // Footer: confirmation step vs target users step
        const footer = !showTargetUsers ? (
            <div style={styles.buttonsContainer}>
                <button
                    className="primary-button"
                    onClick={onClose}
                    style={{ minWidth: '120px' }}
                >
                    Cancel
                </button>
                <button
                    className="primary-button"
                    onClick={this.handleYesClick}
                    style={{ minWidth: '120px' }}
                >
                    Yes
                </button>
            </div>
        ) : (
            <div style={styles.buttonsContainer}>
                <button
                    className="primary-button"
                    onClick={onClose}
                    style={{ minWidth: '120px' }}
                >
                    Cancel
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ display: 'flex', marginRight: '5px' }}>
                        {isSubmitting && (<Loader height={20} width={20} custom={true} />)}
                    </div>
                    <button
                        className="primary-button"
                        onClick={this.handleSubmit}
                        disabled={!hasSelectedUsers || isSubmitting}
                        style={{
                            opacity: !hasSelectedUsers || isSubmitting ? 0.5 : 1,
                            cursor: !hasSelectedUsers || isSubmitting ? 'not-allowed' : 'pointer',
                            minWidth: '120px',
                        }}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                </div>
            </div>
        );

        return (
            <CommonModal
                show={isOpen}
                onClose={onClose}
                title=""
                subTitle=""
                width="1000px"
                closeOnBackdropClick={!isSubmitting}
                footer={footer}
            >
                {/* Custom header with icon */}
                {headerContent}

                <p style={styles.message}>
                    Review the user mapping request that will be applied.{showTargetUsers && " Select users to send chain access request to System Admin."}
                </p>

                <div style={{ borderBottom: "1px solid #F0F2F5", marginBottom: "15px" }} />

                {hasUnmappedUsers && (
                    <>
                        <div
                            style={{
                                ...styles.infoPill,
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                            }}
                        >
                            <span
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    border: "2px solid #0755AA",
                                    color: "#0755AA",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 11,
                                    fontWeight: "bold",
                                    lineHeight: 1,
                                    flexShrink: 0,
                                }}
                            >
                                !
                            </span>
                            <span>
                                Submit a request to map this user(s) {chainName && <>to chain <strong>{chainName}</strong></>}
                            </span>
                        </div>

                        {/* Confirmation step: show email-only table + question */}
                        {!showTargetUsers ? (
                            <>
                                <div style={styles.tableContainer}>
                                    <LightThemeTable
                                        data={confirmationData}
                                        columns={confirmationColumns}
                                        pageSizeOptions={[5, 10, 20, 50]}
                                        isLoading={false}
                                    />
                                </div>
                                <p style={{ ...styles.message, fontSize: "14px", color: "#374151", marginTop: "8px" }}>
                                    Do you want to submit a mapping request for the user(s)?
                                </p>
                            </>
                        ) : (
                            /* Target users step: full editable table */
                            <div style={styles.tableContainer}>
                                <h2 style={{ fontSize: "14px", margin: "0 0 12px 0", fontWeight: 600 }}>
                                    Target Users
                                </h2>
                                <LightThemeTable
                                    data={tableData}
                                    columns={columns}
                                    pageSizeOptions={[5, 10, 20, 50]}
                                    isLoading={isLoading}
                                />
                            </div>
                        )}
                    </>
                )}
            </CommonModal>
        );
    }
}

const mapStateToProps = (state: State) => ({
    roles: state.chains_users_role.roles,
    authUser: state.auth_user.content,
});

const mapDispatchToProps = {
    getRoles,
    createRequest,
};

const connecter = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connecter>;

export default connecter(ExternalAdminChainRequestModal);